import type { OrchestrationEvent } from "@t3tools/contracts";

type ThreadMessageSentEvent = Extract<OrchestrationEvent, { type: "thread.message-sent" }>;
type ThreadActivityAppendedEvent = Extract<
  OrchestrationEvent,
  { type: "thread.activity-appended" }
>;
type ThreadProposedPlanUpsertedEvent = Extract<
  OrchestrationEvent,
  { type: "thread.proposed-plan-upserted" }
>;
export type IncrementalThreadEvent =
  | ThreadActivityAppendedEvent
  | ThreadMessageSentEvent
  | ThreadProposedPlanUpsertedEvent;
export type ThreadEventRoute = "apply" | "buffer" | "ignore" | "replay" | "snapshot";

const INCREMENTAL_ACTIVITY_KINDS = new Set([
  "approval.requested",
  "approval.resolved",
  "command.completed",
  "command.started",
  "file.changed",
  "tool.called",
  "tool.completed",
  "turn.plan.updated",
  "user-input.requested",
  "user-input.resolved",
]);

export function isIncrementalThreadEvent(
  event: OrchestrationEvent,
): event is IncrementalThreadEvent {
  if (event.type === "thread.message-sent" || event.type === "thread.proposed-plan-upserted") {
    return true;
  }

  if (event.type !== "thread.activity-appended") {
    return false;
  }

  return INCREMENTAL_ACTIVITY_KINDS.has(event.payload.activity.kind);
}

export function mergeOrchestrationEvents(
  ...groups: ReadonlyArray<ReadonlyArray<OrchestrationEvent>>
): OrchestrationEvent[] {
  const eventBySequence = new Map<number, OrchestrationEvent>();
  for (const group of groups) {
    for (const event of group) {
      eventBySequence.set(event.sequence, event);
    }
  }
  return [...eventBySequence.values()].sort((left, right) => left.sequence - right.sequence);
}

export function routeActiveThreadEvent(input: {
  busy: boolean;
  event: OrchestrationEvent;
  streamSequence: number;
  threadId: string;
}): ThreadEventRoute {
  const { busy, event, streamSequence, threadId } = input;
  if (event.aggregateKind !== "thread" || event.aggregateId !== threadId) {
    return "ignore";
  }
  if (busy) {
    return "buffer";
  }
  if (event.sequence <= streamSequence) {
    return "ignore";
  }
  if (event.sequence > streamSequence + 1) {
    return "replay";
  }
  return isIncrementalThreadEvent(event) ? "apply" : "snapshot";
}
